import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { CTABand } from "../components/Shared";
import { IMAGES, CONTACT, ACCREDITATION, PARTNERS, WHY_CAREFIRST } from "../mock";
import { SERVICES } from "../data/services";
import { AREAS } from "../data/areas";
import { Button } from "../components/ui/button";
import { HeartHandshake, ClipboardPlus, Phone, ArrowRight, ShieldCheck, Users, Home as HomeIcon, HeartPulse, Star } from "lucide-react";

const BASE = "https://carefirstoh.com";

const whyIcon = (name) => {
  const p = { size: 28, className: "text-white", strokeWidth: 2 };
  switch (name) { case "users": return <Users {...p} />; case "shield": return <ShieldCheck {...p} />; case "home": return <HomeIcon {...p} />; case "heart": return <HeartPulse {...p} />; default: return null; }
};

const schema = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  name: "CareFirst Home Health Services LLC",
  image: `${BASE}/carefirst-logo.png`,
  url: BASE,
  telephone: CONTACT.phone,
  email: CONTACT.email,
  address: { "@type": "PostalAddress", streetAddress: "6740 Huntley Rd #107", addressLocality: "Columbus", addressRegion: "OH", postalCode: "43229", addressCountry: "US" },
  areaServed: AREAS.map((a) => ({ "@type": "City", name: `${a.name}, OH` })),
  medicalSpecialty: "Home Health Care",
};

const Home = () => (
  <div>
    <Seo title="Home Health Care in Columbus, OH | CareFirst Home Health Services" description="CareFirst Home Health Services is a CHAP-accredited home health agency in Columbus, Ohio offering skilled nursing, home health aides, personal care and more. Medicare, Medicaid, PASSPORT, DODD & VA welcome." canonical={BASE + "/"} jsonLd={schema} />

    {/* Hero */}
    <section className="relative">
      <div className="absolute inset-0">
        <img src={IMAGES.nurseSmiling} alt="Compassionate caregiver assisting a senior at home in Columbus, Ohio" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,42,94,0.92) 0%, rgba(22,53,122,0.7) 45%, rgba(22,53,122,0.25) 100%)" }} />
      </div>
      <div className="relative max-w-[1240px] mx-auto px-4 py-24 md:py-32">
        <div className="max-w-[640px] text-white">
          <span className="inline-block bg-[#4ca82f] text-white text-[12px] font-bold tracking-wide px-4 py-1.5 rounded-full mb-5">CHAP ACCREDITED · COLUMBUS, OHIO</span>
          <h1 className="font-display text-[40px] md:text-[54px] font-semibold leading-[1.1]">Compassionate Home Health Care You Can Trust</h1>
          <p className="text-white/90 text-[18px] mt-5 leading-relaxed">CareFirst provides personalized skilled nursing and supportive care in the comfort of your own home — helping patients recover, stay independent, and live with dignity across Central Ohio.</p>
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button asChild className="bg-[#4ca82f] hover:bg-[#3d8a26] text-white font-semibold px-7 py-6 rounded-full text-[15px]"><Link to="/contact"><HeartHandshake size={18} className="mr-2" /> Request Care</Link></Button>
            <Button asChild className="bg-white hover:bg-gray-100 text-[#16357a] font-semibold px-7 py-6 rounded-full text-[15px]"><Link to="/referral"><ClipboardPlus size={18} className="mr-2" /> Make a Referral</Link></Button>
          </div>
          <a href={`tel:${CONTACT.phoneRaw}`} className="inline-flex items-center gap-2 mt-6 text-white font-semibold hover:text-[#7ed957]"><Phone size={18} /> Call {CONTACT.phone}</a>
        </div>
      </div>
    </section>

    {/* Referral highlight strip */}
    <section className="bg-[#16357a] text-white py-6">
      <div className="max-w-[1240px] mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-[16px] font-semibold flex items-center gap-2"><ClipboardPlus size={20} className="text-[#7ed957]" /> Physicians, hospitals, case managers & social workers — send a referral in minutes.</p>
        <Link to="/referral" className="inline-flex items-center gap-2 bg-[#4ca82f] hover:bg-[#3d8a26] font-semibold px-6 py-3 rounded-full text-[14px] transition-colors">Make a Referral <ArrowRight size={16} /></Link>
      </div>
    </section>

    {/* Services */}
    <section className="bg-white py-20">
      <div className="max-w-[1240px] mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display text-[#16357a] text-[34px] md:text-[42px] font-semibold mb-3">Our Home Health Services</h2>
          <p className="text-[#4a4a4a] text-[17px] max-w-[640px] mx-auto">Skilled and non-skilled care tailored to each patient, delivered right at home.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.slice(0, 9).map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} className="group rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6">
              <h3 className="font-display text-[#16357a] text-[21px] font-semibold mb-2 group-hover:text-[#4ca82f] transition-colors">{s.name}</h3>
              <p className="text-[#666] text-[14px] leading-relaxed mb-4">{s.short}</p>
              <span className="inline-flex items-center gap-1 text-[#4ca82f] font-semibold text-[14px]">Learn more <ArrowRight size={15} /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* Why choose us */}
    <section className="bg-[#eef6ea] py-20">
      <div className="max-w-[1240px] mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display text-[#16357a] text-[34px] md:text-[42px] font-semibold mb-3">Why Families & Providers Choose CareFirst</h2>
          <p className="text-[#4a4a4a] text-[17px] max-w-[640px] mx-auto">Compassionate, patient-centered care backed by professional standards.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_CAREFIRST.map((w) => (
            <div key={w.text} className="bg-white rounded-2xl p-7 text-center shadow-sm hover:shadow-lg transition-all flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-[#16357a] flex items-center justify-center mb-4">{whyIcon(w.icon)}</div>
              <p className="font-semibold text-[#333] text-[15px] leading-snug">{w.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Accreditation + partners */}
    <section className="bg-white py-20">
      <div className="max-w-[1240px] mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-14 items-center mb-16">
          <div>
            <h2 className="font-display text-[#16357a] text-[32px] md:text-[40px] font-semibold leading-tight mb-5">{ACCREDITATION.heading}</h2>
            <div className="w-16 h-1 bg-[#4ca82f] mb-6 rounded-full" />
            <p className="text-[#4a4a4a] text-[16px] leading-relaxed">{ACCREDITATION.body}</p>
          </div>
          <div className="flex justify-center"><img src={ACCREDITATION.seal} alt="CHAP Seal of Accreditation - Home Health" className="max-w-[400px] w-full" /></div>
        </div>
        <div className="pt-10 border-t border-gray-100">
          <p className="text-center text-[#16357a] font-semibold text-[15px] tracking-wide uppercase mb-8">Proudly Accepted By &amp; Partnered With</p>
          <div className="flex flex-col items-center gap-8">
            <img src={PARTNERS.grid} alt="Molina Healthcare, Ohio Department of Aging, Ohio Department of Developmental Disabilities, Anthem, CareSource" className="max-w-[820px] w-full opacity-90" />
            <div className="flex flex-wrap justify-center items-center gap-x-14 gap-y-6">
              <img src={PARTNERS.wellcare} alt="Wellcare by Buckeye Health Plan" className="max-w-[210px] w-full opacity-90" />
              <img src={PARTNERS.medicaid} alt="Ohio Department of Medicaid - Next Generation MyCare" className="max-w-[230px] w-full opacity-90" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Service areas */}
    <section className="bg-[#eef6ea] py-20">
      <div className="max-w-[1240px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="font-display text-[#16357a] text-[34px] md:text-[42px] font-semibold mb-3">Serving Columbus & Central Ohio</h2>
          <p className="text-[#4a4a4a] text-[17px] max-w-[640px] mx-auto">Trusted home health care across the communities we call home.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {AREAS.map((a) => (
            <Link key={a.slug} to={`/service-areas/${a.slug}`} className="px-5 py-2 rounded-full bg-white border border-[#16357a]/20 text-[#16357a] font-semibold text-[14px] hover:bg-[#16357a] hover:text-white transition-colors">{a.name}, OH</Link>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonials (placeholder) */}
    <section className="bg-white py-20">
      <div className="max-w-[1240px] mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-display text-[#16357a] text-[34px] md:text-[42px] font-semibold mb-3">What Families Say</h2>
          <p className="text-[#4a4a4a] text-[15px] max-w-[640px] mx-auto">Placeholder testimonials — to be replaced with real client and family reviews.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-gray-100 shadow-sm p-7">
              <div className="flex gap-1 mb-3">{[...Array(5)].map((_, k) => <Star key={k} size={16} className="text-[#4ca82f] fill-[#4ca82f]" />)}</div>
              <p className="text-[#555] text-[15px] italic leading-relaxed mb-4">“[Placeholder testimonial — add a real quote from a satisfied client or family member here.]”</p>
              <p className="font-semibold text-[#16357a] text-[14px]">— Client Family, Columbus, OH</p>
            </div>
          ))}
        </div>
        <p className="text-center text-[#4a4a4a] text-[15px] mt-8">Happy with your care? <a href="#" className="text-[#4ca82f] font-semibold hover:underline">Leave us a Google review</a>.</p>
      </div>
    </section>

    <CTABand />
  </div>
);

export default Home;
